import { ErrorBuffer } from './ErrorBuffer';

/**
 * Handles terminal error emission and tracking
 * Centralizes all error emission and tracking logic
 */
export class TerminalErrorEmitter {
  /**
   * Remove the processed errors set since we don't want to deduplicate errors
   * private processedErrors = new Set<string>();
   */
  private errorState = false;
  private lastPromptTime = 0;
  private errorBuffer?: ErrorBuffer;

  constructor(errorBuffer?: ErrorBuffer) {
    this.errorBuffer = errorBuffer;
  }

  /**
   * Process terminal output for shell prompts
   * @param output The terminal output to check
   */
  processShellPrompt(output: string): boolean {
    console.log('Processing shell prompt for:', { output });

    // Check for shell prompt pattern
    if (/~\/project\s*\n❯/.test(output)) {
      console.log('Shell prompt detected, clearing previous errors');

      /**
       * No more processed errors to clear
       * this.clearProcessedErrors();
       */
      this.setErrorState(false);
      this.lastPromptTime = Date.now();

      return true;
    }

    console.log('No shell prompt detected');

    return false;
  }

  /**
   * Determine if an error is fresh (occurring after the last prompt)
   */
  isFreshError(errorTime: number): boolean {
    return errorTime > this.lastPromptTime;
  }

  /**
   * Emits a terminal error event with improved formatting
   */
  emitTerminalError(output: string, exitCode: number, command: string): void {
    console.log('Emitting terminal error:', { command, exitCode });

    try {
      // Check if we have a shell prompt in this output first
      if (this.processShellPrompt(output)) {
        console.log('Shell prompt detected in error output, skipping error emission');
        return;
      }

      // Create error timestamp
      const timestamp = Date.now();

      // Skip if not a fresh error
      if (this.lastPromptTime > 0 && !this.isFreshError(timestamp)) {
        console.log('Skipping stale error from previous session');
        return;
      }

      // Clean output if we have an ErrorBuffer
      let cleanedOutput = output;
      let errorMessage = '';

      if (this.errorBuffer) {
        cleanedOutput = this.errorBuffer.cleanTerminalForErrors(output);
        errorMessage = this.errorBuffer.extractErrorMessage(output);
      } else {
        // Basic error extraction if no ErrorBuffer
        const errorLines = output
          .split('\n')
          .filter((line) => /(error|failed|unable to|exception|TypeError|ReferenceError|SyntaxError)/i.test(line));
        errorMessage = errorLines.length > 0 ? errorLines.join('\n') : `Command failed with exit code ${exitCode}`;
      }

      /**
       * Removing fingerprinting code
       * const errorFingerprint = this.getErrorFingerprint(errorMessage, command);
       */

      /**
       * Skip if we've already processed this error - removed to allow duplicates
       * if (this.processedErrors.has(errorFingerprint)) {
       *   console.log('Duplicate error, skipping');
       *   return;
       * }
       */

      // Determine severity based on patterns and exit code
      const severity =
        exitCode > 1 || /(cannot find module|TypeError|ReferenceError|SyntaxError)/i.test(cleanedOutput)
          ? 'error'
          : exitCode === 1
            ? 'warning'
            : 'info';

      const terminalError = {
        type: 'TERMINAL_ERROR',
        command,
        message: errorMessage,
        stack: cleanedOutput,
        timestamp,
        severity,
        exitCode,
      };

      console.log('Terminal error:', terminalError);

      this.errorState = true;

      /**
       * Don't track processed errors
       * this.processedErrors.add(errorFingerprint);
       */

      const event = new CustomEvent('dualite-terminal-error', {
        detail: terminalError,
      });

      document.dispatchEvent(event);
      console.log('Terminal error emitted:', terminalError);
    } catch (error) {
      console.error('Error emitting terminal error event:', error);
    }
  }

  /**
   * Emits an error resolution event
   */
  emitErrorResolution(command: string): void {
    console.log('Emitting error resolution for command:', command);

    try {
      const event = new CustomEvent('dualite-terminal-error-resolved', {
        detail: { command, timestamp: Date.now() },
      });

      document.dispatchEvent(event);

      /**
       * No need to clear processed errors
       * this.processedErrors.clear();
       */
      console.log('Error resolution emitted');
    } catch (error) {
      console.error('Error emitting error resolution event:', error);
    }
  }

  /**
   * Resets the error state
   * Called when user manually fixes errors
   */
  resetErrorState(): void {
    console.log('Resetting error state');

    try {
      this.errorState = false;

      /**
       * No need to clear processed errors
       * this.processedErrors.clear();
       */

      const event = new CustomEvent('dualite-terminal-error-resolved', {
        detail: { source: 'manual-reset', timestamp: Date.now() },
      });

      document.dispatchEvent(event);
      console.log('Error state reset complete');
    } catch (error) {
      console.error('Error resetting terminal error state:', error);
    }
  }

  /**
   * Removing all fingerprinting methods
   *
   * Creates a unique fingerprint for an error:
   * getErrorFingerprint(output: string, command: string): string { ... }
   *
   * Check if error has already been processed:
   * hasProcessedError(fingerprint: string): boolean { return this.processedErrors.has(fingerprint); }
   *
   * Mark an error as processed:
   * markErrorAsProcessed(fingerprint: string): void { this.processedErrors.add(fingerprint); }
   *
   * Clear all processed errors:
   * clearProcessedErrors(): void { this.processedErrors.clear(); }
   */

  /**
   * Get error state
   */
  getErrorState(): boolean {
    return this.errorState;
  }

  /**
   * Set error state
   */
  setErrorState(state: boolean): void {
    this.errorState = state;
  }

  /**
   * Set the error buffer
   */
  setErrorBuffer(buffer: ErrorBuffer): void {
    this.errorBuffer = buffer;
  }
}
